
import { useState, useEffect } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { useLocation, useNavigate } from 'react-router-dom';
import LoginPage from './LoginPage';
import { apiFetch } from '@/hooks/useApi';

// Pages that agent/agent_admin roles are allowed to access
const AGENT_ALLOWED_PATHS = ['/agent-assist', '/agent-assist-admin'];

// agent_admin can access /agent-assist-admin, plain agent cannot
const AGENT_ADMIN_PATHS = ['/agent-assist-admin'];

export default function AuthGuard({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useIsAuthenticated();
  const { inProgress } = useMsal();
  const [userRole, setUserRole] = useState<string | null>(null);
  const [checkDone, setCheckDone] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated || inProgress !== InteractionStatus.None) return;

    // Check for returnTo from session expired redirect
    const returnTo = sessionStorage.getItem('loginReturnTo');
    if (returnTo) {
      sessionStorage.removeItem('loginReturnTo');
      window.location.href = returnTo;
      return;
    }

    apiFetch('/api/auth/me')
      .then(res => res.json())
      .then(data => {
        const role = data.role || 'viewer';
        setUserRole(role);
        setCheckDone(true);

        // After login: redirect agent/agent_admin to /agent-assist if on a page they can't access
        if (role === 'agent' || role === 'agent_admin') {
          const isAllowed = AGENT_ALLOWED_PATHS.some(p => pathname.startsWith(p));
          // agent cannot access agent-assist-admin
          const isAdminPage = AGENT_ADMIN_PATHS.some(p => pathname.startsWith(p));
          if (!isAllowed || (role === 'agent' && isAdminPage)) {
            navigate('/agent-assist', { replace: true });
          }
        }
      })
      .catch(() => {
        setUserRole('viewer');
        setCheckDone(true);
      });
  }, [isAuthenticated, inProgress]);

  // Enforce route restrictions on client-side navigation
  useEffect(() => {
    if (!checkDone || !userRole) return;
    if (userRole !== 'agent' && userRole !== 'agent_admin') return;

    const isAllowed = AGENT_ALLOWED_PATHS.some(p => pathname.startsWith(p));
    const isAdminPage = AGENT_ADMIN_PATHS.some(p => pathname.startsWith(p));

    if (!isAllowed || (userRole === 'agent' && isAdminPage)) {
      navigate('/agent-assist', { replace: true });
    }
  }, [pathname, checkDone, userRole]);

  if (inProgress !== InteractionStatus.None) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div style={{ color: '#6b7280', fontSize: '14px' }}>Authenticating...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  // Wait for role check
  if (!checkDone) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div style={{ color: '#6b7280', fontSize: '14px' }}>Loading...</div>
      </div>
    );
  }

  // Pending users see a full-page message
  if (userRole === 'pending') {
    return (
      <div className="flex items-center justify-center h-screen bg-[var(--bg-page)]">
        <div className="bg-[var(--bg-card)] rounded-2xl border border-[var(--border-default)] shadow-lg p-10 max-w-md text-center space-y-4">
          <div className="w-16 h-16 rounded-2xl bg-amber-100 flex items-center justify-center mx-auto">
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-amber-600">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-[var(--text-primary)]">Account Pending Approval</h1>
          <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
            Your account has been created but is not yet approved.
            Please contact your administrator to get access to the analytics platform.
          </p>
          <div className="pt-2">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-50 rounded-lg border border-amber-200">
              <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
              <span className="text-xs font-medium text-amber-700">Pending Review</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
